import React from 'react';
import { formatCurrency } from '../../utils/currencyUtils';
import { Calendar, ChevronDown, ChevronUp, Trash2, User, Wallet } from 'lucide-react';

export default function LabourAdvanceCard({
  payment,
  isExpanded,
  onToggle,
  onDelete,
  onSettleFromWallet,
  employees,
  companyCurrency,
}) {
  const personLabel =
    payment.personType === 'Employee'
      ? employees.find((e) => e.employeeId === payment.employeeId)?.name || payment.employeeId
      : payment.personName || '—';
  const typeTitle = payment.paymentType || 'Labour/Advance';
  const initial = (personLabel || typeTitle || 'L').toString().charAt(0).toUpperCase();

  return (
    <div className="rounded-xl border border-slate-200/90 bg-gradient-to-br from-slate-50/90 to-white shadow-sm overflow-hidden ring-1 ring-slate-200/50">
      <div
        className="p-3.5 cursor-pointer active:bg-slate-50/80 transition-colors"
        onClick={() => onToggle(payment.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onToggle(payment.id)}
      >
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-teal-600 to-teal-700 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md">
            {initial}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-bold text-gray-900 truncate">{typeTitle}</h3>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ring-1 ring-inset ${
                  payment.settled
                    ? 'bg-teal-100 text-teal-800 ring-teal-200'
                    : 'bg-red-100 text-red-800 ring-red-200'
                }`}
              >
                {payment.settled ? 'Settled' : 'Unsettled'}
              </span>
            </div>
            <p className="text-xs text-gray-600 mt-1 flex flex-wrap items-center gap-x-1.5">
              <span className="inline-flex items-center gap-1">
                <User className="w-3 h-3 flex-shrink-0" aria-hidden />
                <span className="truncate max-w-[11rem]">{personLabel}</span>
              </span>
              <span className="text-gray-300">·</span>
              <span className="inline-flex items-center gap-1">
                <Calendar className="w-3 h-3 flex-shrink-0" aria-hidden />
                {payment.date}
              </span>
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <div className="text-right">
              <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Amount</p>
              <p className="text-lg font-bold text-teal-700 leading-tight tabular-nums">
                {formatCurrency(Number(payment.amount) || 0, companyCurrency)}
              </p>
            </div>
            <div className="flex items-center gap-0.5">
              {!payment.settled && onSettleFromWallet && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSettleFromWallet(payment);
                  }}
                  className="p-2 text-teal-700 hover:bg-teal-50 rounded-full transition-colors"
                  title="Settle from employee wallet"
                >
                  <Wallet className="w-4 h-4" aria-hidden />
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(payment.id);
                }}
                className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" aria-hidden />
              </button>
              <span className="p-2 text-gray-400" aria-hidden>
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </span>
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-slate-200/80 p-4 bg-slate-50/50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-3">
              <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-2">Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500">Date</span>
                  <span className="font-medium text-right">{payment.date || '—'}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500">Person type</span>
                  <span className="font-medium text-right">{payment.personType || '—'}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500">Payment type</span>
                  <span className="font-medium text-right">{payment.paymentType || '—'}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500">Payment mode</span>
                  <span className="font-medium text-right">{payment.paymentMode || 'Cash'}</span>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-3">
              <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-2">
                Person & settlement
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500">Name</span>
                  <span className="font-medium text-right">{personLabel}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500">Employee ID</span>
                  <span className="font-medium text-right">{payment.employeeId || '—'}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500">Status</span>
                  <span
                    className={`font-semibold text-right ${payment.settled ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {payment.settled ? 'Settled' : 'Unsettled'}
                  </span>
                </div>
                {payment.settled && (
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-500">Settled in</span>
                    <span className="font-medium text-right text-green-600">{payment.appliedInMonth || '—'}</span>
                  </div>
                )}
                {payment.settlementRemark && (
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-500">Settlement note</span>
                    <span className="font-medium text-right break-words max-w-[55%]">
                      {payment.settlementRemark}
                    </span>
                  </div>
                )}
                {payment.settledFromOtherWallet?.amount > 0 && (
                  <div className="rounded-lg bg-teal-50 border border-teal-100 px-2 py-2 text-xs text-teal-900">
                    <span className="font-semibold">From wallet:</span>{' '}
                    {payment.settledFromOtherWallet.sourceEmployeeName} —{' '}
                    {formatCurrency(Number(payment.settledFromOtherWallet.amount), companyCurrency)}
                  </div>
                )}
                {payment.remark && (
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-500">Remark</span>
                    <span className="font-medium text-right break-words max-w-[55%]">{payment.remark}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
